"use client";

import { unwrapResult } from "@reduxjs/toolkit";
import dynamic from "next/dynamic";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import FadeAnimation from "@/components/atoms/animation/fade-animation";
import TransformAnimation from "@/components/atoms/animation/transform-animation";
import {
  AnnualMultiTrip,
  CoverEdit,
  NotSure,
  OptimizelyExperimentsEnum,
  QuestionTwo,
  SingleTrip,
} from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import { PRODUCT_DOMAIN } from "@/constants";
import cookieHealthCheck from "@/hooks/api/cookie/health-check-cookie";
import useGtmQuestionSet from "@/hooks/gtm/use-gtm-question-set";
import useIsClient from "@/hooks/use-is-client/use-is-client";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useStickyFooter } from "@/hooks/use-media-query/use-media-query-enhanced";
import useReplaceRouterPath from "@/hooks/use-replace-router-path/use-replace-router-path";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { useAppDispatch } from "@/store/hook";
import {
  saveQuestionDataBFF,
  sessionCheck,
} from "@/store/slices/quote/quote.action";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { RootState } from "@/store/store";
import { CoverTypeContainerProps } from "@/types/CoverType";
import { QuotePayload } from "@/types/quote/quote-data";
import { TripDetails } from "@/types/TripDetails";
import { modifyPayloadSave } from "@/utils/common.utils";
import {
  findAnswerById,
  modifyCoverTypeButtonArray,
} from "@/utils/cover-type.utils";
import { handleScrollToRef } from "@/utils/scrollUtils";

import QuestionOneSection from "./question-one-section/question-one-section";
import ActionButton from "../../action-button/action-button";
import editQuoteStyle from "../../quote-section/edit-quote-section/edit-quote.module.css";

const CoverTypeModal = dynamic(
  () => import("./cover-type-modal/cover-type-modal")
);
const initialPayload: QuotePayload = {
  type: "IntermediateData",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
    lastVisitedPage: "test",
  },
};

function CoverTypeContainer({
  formRef,
  ctaRef,
  usagePage,
  pageContent,
}: Readonly<CoverTypeContainerProps>) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const editPage = searchParams.get(CoverEdit);
  const partnership = searchParams.get("partnership");
  const isBcuk = partnership === "bcuk";

  const {
    removeNotSureButton,
    ctaButtonOneLabel,
    ctaButtonTwoLabel,
    ctaButtonThreeLabel,
    cancelEditingBtnText = "Cancel Editing",
    questionText,
    modalEnable,
    modalTitle,
    backgroundModalImageUrl,
    primaryButtonText,
    secondaryButtonText,
    detailTitleOne,
    detailDescriptionOne,
    detailTitleTwo,
    detailDescriptionTwo,
    detailTitleThree,
    detailDescriptionThree,
    coverTypeSupportText,
    backgroundModalMobileImageUrl,
    ctaButtonText,
    modalDescription,
  } = pageContent;

  // State
  const [payload, setPayload] = useState<QuotePayload>(initialPayload);
  const [isSubmitBtnClicked, setIsSubmitBtnClicked] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [singleTypeModalOpen, setSingleTypeModalOpen] = useState(false);
  const [apiLoading, setApiLoading] = useState<boolean>(false);

  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { tripDetails } = quoteResponse?.attributes || undefined;

  // Hooks
  useReplaceRouterPath(pathname);
  const isStickyFooter = useStickyFooter();
  useGtmQuestionSet({
    pageTitle: QuestionTwo,
    tripType: payload?.attributes?.tripDetails?.policyType ?? "",
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    isSubmitBtnClicked,
  });
  const dispatch = useAppDispatch();
  const { current } = useWizard();
  const { selected, setSelectedState } = useQuestionsState();
  const { resetQuestionData } = useResetStateTravelerEdit();
  const isClient = useIsClient();
  const isdesktop = useMediaQuery("(min-width: 600px)");

  const inputRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (quoteResponse?.attributes?.tripDetails?.policyType) {
      if (quoteResponse?.attributes?.tripDetails.policyType === SingleTrip) {
        setSelectedState(1);
      }
      if (
        quoteResponse?.attributes?.tripDetails.policyType === AnnualMultiTrip
      ) {
        setSelectedState(2);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteResponse?.attributes?.tripDetails]);

  const handleScrollTrigger = (selectedAnswer?: number, id?: number) => {
    if (selectedAnswer === 1 || id === 2) {
      setTimeout(() => {
        handleScrollToRef(inputRef, !isStickyFooter);
      }, 100);
    }
  };

  // Memoized Values
  const showondesktop: string = useMemo(
    () => isdesktop.toString(),
    [isdesktop]
  );
  const {
    marketingChannelUrl,
    searchEngReferrer,
  }: { marketingChannelUrl: string; searchEngReferrer: string } =
    useMemo(() => {
      if (!isClient) {
        return { marketingChannelUrl: "", searchEngReferrer: "" };
      }
      return {
        marketingChannelUrl: window.location.href,
        searchEngReferrer: window.document.referrer,
      };
    }, [isClient]);

  const modifiedCoverTypeButtonArray = useMemo(() => {
    return modifyCoverTypeButtonArray(
      ctaButtonOneLabel,
      ctaButtonTwoLabel,
      ctaButtonThreeLabel,
      removeNotSureButton
    );
  }, [
    ctaButtonOneLabel,
    ctaButtonThreeLabel,
    ctaButtonTwoLabel,
    removeNotSureButton,
  ]);

  // Side Effects
  useEffect(() => {
    const getModifiedQuoteResponse = () => {
      if (isBcuk) {
        return {
          ...quoteResponse,
          attributes: {
            ...quoteResponse?.attributes,
            partnerCode: "BCUK",
          },
        };
      }
      return {
        ...quoteResponse,
        attributes: quoteResponse?.attributes || {
          domain: PRODUCT_DOMAIN,
        },
      };
    };
    const modifyTripDetails = (selectedPolicy: string | undefined) => {
      let tripDetailsModified: TripDetails = {
        ...tripDetails,
        policyType: selectedPolicy,
      };
      if (
        tripDetails?.policyType &&
        tripDetails?.policyType !== selectedPolicy
      ) {
        tripDetailsModified = {
          ...tripDetails,
          policyType: selectedPolicy,
          startDate: undefined,
          endDate: undefined,
        };
        if (selectedPolicy === SingleTrip || selectedPolicy === NotSure) {
          tripDetailsModified.area = undefined;
        }

        if (selectedPolicy === AnnualMultiTrip) {
          tripDetailsModified.toLocation = undefined;
        }
      } else {
        tripDetailsModified = {
          ...tripDetails,
          policyType: selectedPolicy,
        };
        if (tripDetails?.area === "" || tripDetails?.area?.toString() === "0") {
          tripDetailsModified.area = undefined;
        }
      }
      return tripDetailsModified;
    };
    if (selected) {
      const selectedPolicy = findAnswerById(selected as number)?.value;
      const modifiedQuoteResponse = getModifiedQuoteResponse();
      const tripDetailsModified = modifyTripDetails(selectedPolicy);
      const marketingChannelDetails = {
        marketingChannelData: marketingChannelUrl,
        referrer: searchEngReferrer,
      };
      setPayload(
        modifyPayloadSave(
          PRODUCT_DOMAIN === "STS" ? modifiedQuoteResponse : quoteResponse,
          tripDetailsModified,
          marketingChannelDetails,
          isBcuk
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, tripDetails]);

  // Handlers
  const accessSelectedAnswer = async (answerNo: number) => {
    // de select the previous selected answer
    if (selected === answerNo) {
      setSelectedState(0);
      return;
    }

    // Handle clicking Single Trip when modal is enabled
    // Works in both normal mode and edit mode (?coverEdit=true)
    if (answerNo === 1 && modalEnable) {
      // Check if AMT to ST modal removal experiment is enabled
      // AND we're switching FROM Annual Multi Trip (2) TO Single Trip (1)
      const isExperimentEnabled =
        typeof window !== "undefined" &&
        localStorage.getItem(
          OptimizelyExperimentsEnum.AMT_TO_ST_MODAL_REMOVAL
        ) === "variant";
      const isDefinitionEnabled =
        current.brandSpecificParams?.amtToStModalRemovalCoverType === true;

      const isHideModalExperimentEnabled =
        isExperimentEnabled && isDefinitionEnabled;

      if (isHideModalExperimentEnabled && selected === 2) {
        // Skip modal and call sessionHealth API directly when switching from AMT to ST
        // This applies to both normal flow and edit flow
        setApiLoading(true);
        try {
          await cookieHealthCheck();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("SessionHealth API error:", error);
        } finally {
          setApiLoading(false);
        }
        setSelectedState(answerNo);
        return;
      }

      // Default behavior: show modal
      setSingleTypeModalOpen(true);
    }

    setSelectedState(answerNo);
  };
  const modalConfirmBtnClick = (answerNo: number) => {
    handleScrollToRef(inputRef, !isStickyFooter);
    if (answerNo) {
      setSelectedState(2);
    }
    setSingleTypeModalOpen(false);
  };
  const closeModalSingleType = () => {
    handleScrollToRef(inputRef, !isStickyFooter);
    setSingleTypeModalOpen(false);
  };
  const handleRedirectAfterSubmission = async () => {
    if (editPage && quoteResponse?.referenceId) {
      router.push(current.edges.toNextCoverEdit ?? "");
    } else if (!quoteResponse?.referenceId) {
      const questionData = await dispatch(
        saveQuestionDataBFF({
          payload: { ...payload, viewPage: Routes.CoverType },
          router,
          dispatch,
        })
      );
      const result = unwrapResult(questionData);
      if (result) router.push(current.next ?? "");
    } else {
      dispatch(sessionCheck({ router, dispatch }));
      router.push(current.next ?? "");
    }
  };
  const handleSubmitButton = async () => {
    if (selected !== 0) {
      setIsSubmitBtnClicked(true);
      setButtonLoading(true);
      try {
        dispatch(setQuestionData(payload));
        await handleRedirectAfterSubmission();
      } finally {
        setButtonLoading(false);
      }
    }
  };
  const handleCancelEdit = () => setModalOpen(true);

  const editFinishStyle = editPage ? editQuoteStyle.editFinishButton : "";

  return (
    <div
      data-testid="question-one-test-id"
      className={` bg-surface-neutral-option-1 rounded-b-radius-xxl  
           md:py-spacing-xxl py-spacing-m px-spacing-m h-full`}
    >
      <div
        data-testid="renderQuestionOneSection-test-id"
        className=" lg:max-w-[414px] md:max-w-[400px] w-full mx-auto relative h-full flex flex-col justify-between"
      >
        <TransformAnimation animationRef={formRef} currentPage={usagePage}>
          <QuestionOneSection
            editPage={editPage}
            cancelEditingBtnText={cancelEditingBtnText}
            questionText={questionText}
            coverTypeSupportText={coverTypeSupportText}
            modalOpen={modalOpen}
            selected={selected}
            // quoteLoading={quoteLoading}
            setModalOpen={setModalOpen}
            handleCancelEdit={handleCancelEdit}
            accessSelectedAnswer={accessSelectedAnswer}
            resetQuestionData={resetQuestionData}
            answerArray={modifiedCoverTypeButtonArray}
            isClient={isClient}
            onScrollTrigger={(selectedAnswer, id, dataTestId) => {
              const buttonElement = document.querySelector(`#${dataTestId}`);
              if (
                buttonElement?.classList.contains(
                  "componnt_lib_toggle_btn_inactive"
                )
              ) {
                handleScrollTrigger(selectedAnswer, id);
              }
            }}
          />
        </TransformAnimation>
        {singleTypeModalOpen && (
          <CoverTypeModal
            isOpen={singleTypeModalOpen}
            modalTitle={modalTitle}
            backgroundModalImageUrl={backgroundModalImageUrl}
            primaryButtonText={primaryButtonText}
            secondaryButtonText={secondaryButtonText}
            detailTitleOne={detailTitleOne}
            detailDescriptionOne={detailDescriptionOne}
            detailTitleTwo={detailTitleTwo}
            detailDescriptionTwo={detailDescriptionTwo}
            detailTitleThree={detailTitleThree}
            detailDescriptionThree={detailDescriptionThree}
            backgroundModalMobileImageUrl={backgroundModalMobileImageUrl}
            showOnDesktop={showondesktop ?? "false"}
            modalDescription={modalDescription ?? ""}
            modalConfirmBtnClick={modalConfirmBtnClick}
            closeModal={closeModalSingleType}
            modalSingleTripBtnClick={handleSubmitButton}
          />
        )}
        <FadeAnimation animationRef={ctaRef}>
          <div className={`${editFinishStyle}`} ref={inputRef}>
            <ActionButton
              page={usagePage}
              ctaButtonText={ctaButtonText}
              disableMain={selected === 0}
              isLoading={buttonLoading || apiLoading}
              showBackBtn={false}
              submitButtonClick={handleSubmitButton}
            />
          </div>
        </FadeAnimation>
      </div>
    </div>
  );
}
export default CoverTypeContainer;
